import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, BellRinging, DeviceMobile, Desktop, ArrowLeft,
  CheckCircle, XCircle, PaperPlaneTilt, Gear, Moon
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { usePushNotifications } from '../hooks/usePushNotifications';

export default function PushSettingsPage() {
  const navigate = useNavigate();
  const { supported, permission, isSubscribed, loading, subscribe, unsubscribe, testPush } = usePushNotifications();
  const [testing, setTesting] = useState(false);

  const handleSubscribe = async () => {
    const sub = await subscribe();
    if (sub) toast.success('Push notifications enabled!');
    else if (permission === 'denied') toast.error('Notifications blocked — enable in browser settings');
  };

  const handleUnsubscribe = async () => {
    await unsubscribe();
    toast.success('Push notifications disabled');
  };

  const handleTest = async () => {
    setTesting(true);
    const result = await testPush();
    if (result?.sent_to_devices > 0) toast.success('Test push sent!');
    else toast.info('No devices registered — enable push first');
    setTesting(false);
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="push-settings-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[12px] text-[#555] hover:text-white mb-4 transition-colors">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Push Notifications</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Get real-time alerts on your device</p>
      </div>

      {/* Status Card */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSubscribed ? 'bg-[#10B981]/10' : 'bg-white/[0.04]'}`}>
            {isSubscribed ? <BellRinging size={20} weight="duotone" className="text-[#10B981]" /> : <Bell size={20} weight="duotone" className="text-[#555]" />}
          </div>
          <div>
            <p className="text-[14px] font-semibold text-white">
              {isSubscribed ? 'Push Notifications Active' : 'Push Notifications Off'}
            </p>
            <p className="text-[11px] text-[#555]">
              {!supported ? 'Not supported in this browser' :
               permission === 'denied' ? 'Blocked — enable in browser settings' :
               isSubscribed ? 'You\'ll receive alerts for sessions, tournaments, and more' :
               'Enable to get real-time alerts'}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          {!isSubscribed ? (
            <Button onClick={handleSubscribe} disabled={loading || !supported || permission === 'denied'}
              className="flex-1 h-9 bg-[#10B981] hover:bg-[#059669] text-white text-[12px] font-semibold"
              data-testid="enable-push-btn">
              <BellRinging size={14} className="mr-1" /> {loading ? 'Enabling...' : 'Enable Push Notifications'}
            </Button>
          ) : (
            <>
              <Button onClick={handleTest} disabled={testing}
                className="flex-1 h-9 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px]"
                data-testid="test-push-btn">
                <PaperPlaneTilt size={14} className="mr-1" /> {testing ? 'Sending...' : 'Send Test'}
              </Button>
              <Button variant="outline" onClick={handleUnsubscribe} disabled={loading}
                className="h-9 px-4 bg-transparent border-[#EF4444]/30 text-[#EF4444] text-[12px] hover:bg-[#EF4444]/10"
                data-testid="disable-push-btn">
                Disable
              </Button>
            </>
          )}
        </div>
      </div>

      {/* What you'll get */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5 mb-4">
        <h2 className="text-[14px] font-bold text-white mb-3 flex items-center gap-2">
          <Bell size={16} weight="duotone" className="text-[#9D4CDD]" /> What You'll Receive
        </h2>
        <div className="space-y-2">
          {[
            { label: 'Session invitations & open seats', icon: '🎲' },
            { label: 'Tournament reminders & bracket updates', icon: '🏆' },
            { label: 'League standings & season alerts', icon: '📊' },
            { label: 'Recurring group reminders', icon: '📅' },
            { label: 'New messages', icon: '💬' },
            { label: 'Favorite shop events', icon: '🏪' },
            { label: 'Check-in & waitlist promotions', icon: '✅' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/[0.02] border border-white/[0.04]">
              <span className="text-[14px]">{item.icon}</span>
              <span className="text-[12px] text-[#888]">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Device Info */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
        <h2 className="text-[14px] font-bold text-white mb-3 flex items-center gap-2">
          <Gear size={16} weight="duotone" className="text-[#888]" /> Device Info
        </h2>
        <div className="space-y-1.5 text-[12px]">
          <div className="flex justify-between text-[#555]">
            <span>Browser Support</span>
            <span className={supported ? 'text-[#10B981]' : 'text-[#EF4444]'}>{supported ? 'Supported' : 'Not Supported'}</span>
          </div>
          <div className="flex justify-between text-[#555]">
            <span>Permission</span>
            <span className={permission === 'granted' ? 'text-[#10B981]' : permission === 'denied' ? 'text-[#EF4444]' : 'text-[#F59E0B]'}>
              {permission}
            </span>
          </div>
          <div className="flex justify-between text-[#555]">
            <span>Subscription</span>
            <span className={isSubscribed ? 'text-[#10B981]' : 'text-[#555]'}>{isSubscribed ? 'Active' : 'None'}</span>
          </div>
          <div className="flex justify-between text-[#555]">
            <span>Platform</span>
            <span className="text-[#888]">{navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
